import React, { useContext, useEffect, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { useQuery } from "@apollo/client";
import { getLang } from "src/utils/language";
import { GET_NEWS_DETAILS_BY_ID, GET_NEWS_EDIT_REALESTATES } from "src/api/news";
import { GetNewsDetailsById, GetNewsDetailsByIdVariables } from "src/api/generated/GetNewsDetailsById";
import { GetNewsEditRealestates, GetNewsEditRealestatesVariables } from "src/api/generated/GetNewsEditRealestates";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import * as Screens from "src/screens";
import { getNewsOriginRoute, getNewsPreviewRoute } from "src/utils/NewsRoutes";
import { withRouter, useParams } from "react-router";
import { NewsEditForm } from "./form/NewsEditForm";
import { NewsEditReadOnly } from "./NewsEditReadOnly";
import { getInitialNewsRealestateSelection } from "./form/NewsEditForm.utils";

// Without realestateid the mask was opened from the page "News".
interface Params {
    realestateid?: string;
    newsid: string;
    newspreviewnewsid?: string;
}

const NewsEditBase = () => {
    const { realestateid, newsid, newspreviewnewsid } = useParams<Params>();

    const { t } = useTranslation();
    const { navStore, authStore, uiStore } = useContext(RootStoreContext);

    useEffect(() => {
        if (newspreviewnewsid) {
            navStore.setBackbutton(
                getNewsPreviewRoute(newsid, realestateid, authStore.user?.role),
                t("screens.realestate.news_preview.title").toString()
            );
        } else {
            navStore.setBackbutton(
                getNewsOriginRoute(realestateid, authStore.user?.role),
                realestateid ? t("screens.realestate.dashboard.title").toString() : t("screens.news.title").toString()
            );
        }
    }, [authStore.user?.role, navStore, realestateid, t, newsid, newspreviewnewsid]);

    // Within a realestate the realestate screen sets the title.
    useEffect(() => {
        if (!realestateid) {
            navStore.setTitle(t("screens.news.title"));
        }
    }, [navStore, realestateid, t]);

    const { data, loading, error, refetch } = useQuery<GetNewsDetailsById, GetNewsDetailsByIdVariables>(
        GET_NEWS_DETAILS_BY_ID,
        {
            variables: {
                newsid: newsid,
                language: getLang()
            }
        }
    );

    useEffect(() => {
        refetch();
    }, [uiStore.dbNewsFiles, refetch]);

    // Also loaded without the feature flag: the realestates tell whether the news is for coowners or tenants.
    const {
        data: realestatesData,
        loading: isLoadingRealestates,
        error: realestatesError
    } = useQuery<GetNewsEditRealestates, GetNewsEditRealestatesVariables>(GET_NEWS_EDIT_REALESTATES, {
        variables: { newsid, realestateids: realestateid ? [realestateid] : [] },
        fetchPolicy: "no-cache"
    });

    const initialRealestateSelection = useMemo(
        () => (realestatesData ? getInitialNewsRealestateSelection(realestatesData) : undefined),
        [realestatesData]
    );

    if (loading || isLoadingRealestates) {
        return <Screens.Loading />;
    }
    if (error) {
        return <Screens.Error message={error.message} networkError={error.networkError} />;
    }
    if (realestatesError) {
        return <Screens.Error message={realestatesError.message} networkError={realestatesError.networkError} />;
    }
    if (!data || !initialRealestateSelection) {
        return <Screens.Error message={t("error.nodata").toString()} />;
    }

    const newsDetails = data.newsitems.length > 0 ? data.newsitems[0] : undefined;

    if (initialRealestateSelection.isReadOnly) {
        return <NewsEditReadOnly newsDetails={newsDetails} />;
    }

    return (
        <NewsEditForm
            newsDetails={newsDetails}
            newsid={newsid}
            realestateid={realestateid}
            initialRealestateSelection={initialRealestateSelection}
            refetchData={refetch}
        />
    );
};

export const NewsEdit = withRouter(Sentry.withProfiler(observer(NewsEditBase)));
